/** Jump-cut blank pauses for REEL&SHORTS from measured audio silence. */

export const DEFAULT_MIN_PAUSE_SECONDS = 0.45;
export const DEFAULT_PAD_SECONDS = 0.08;
export const MIN_KEEP_SECONDS = 0.25;

export const PAUSE_CUT_PRESETS = [
  { id: 'tight', label: 'Tight (0.3s)', minPause: 0.3 },
  { id: 'normal', label: 'Normal (0.45s)', minPause: 0.45 },
  { id: 'loose', label: 'Loose (0.7s)', minPause: 0.7 },
];

export function sanitizePauseCut(raw) {
  const data = raw && typeof raw === 'object' ? raw : {};
  const minPause = Number(data.min_pause_seconds);
  const pad = Number(data.pad_seconds);
  return {
    enabled: Boolean(data.enabled),
    min_pause_seconds: Math.round(
      Math.min(2, Math.max(0.2, Number.isFinite(minPause) ? minPause : DEFAULT_MIN_PAUSE_SECONDS)) * 1000,
    ) / 1000,
    pad_seconds: Math.round(
      Math.min(0.25, Math.max(0, Number.isFinite(pad) ? pad : DEFAULT_PAD_SECONDS)) * 1000,
    ) / 1000,
  };
}

export function snippetDurationSeconds(snippet) {
  const dur = Number(snippet?.duration_seconds);
  return Math.max(0.25, Number.isFinite(dur) ? dur : 0.25);
}

export function parseWordTimings(raw, duration) {
  const dur = Math.max(0.25, Number(duration) || 0.25);
  const rows = [];
  (Array.isArray(raw) ? raw : []).forEach((item) => {
    if (!item || typeof item !== 'object') return;
    const token = String(item.word || '').trim();
    if (!token) return;
    const start = Math.max(0, Number(item.start) || 0);
    const end = Math.max(start + 0.05, Math.min(dur, Number(item.end) || start + 0.12));
    rows.push({ word: token, start, end });
  });
  rows.sort((a, b) => a.start - b.start || a.end - b.end);
  return rows;
}

export function parseSilenceIntervals(raw, duration) {
  const dur = Math.max(0, Number(duration) || 0);
  const rows = [];
  (Array.isArray(raw) ? raw : []).forEach((item) => {
    if (!item || typeof item !== 'object') return;
    const start = Math.max(0, Number(item.start) || 0);
    let end = Number(item.end);
    if (!Number.isFinite(end)) return;
    if (dur) end = Math.min(dur, end);
    if (end - start < 0.05) return;
    rows.push({
      start: Math.round(start * 1000) / 1000,
      end: Math.round(end * 1000) / 1000,
    });
  });
  rows.sort((a, b) => a.start - b.start || a.end - b.end);
  const merged = [];
  rows.forEach((row) => {
    if (merged.length && row.start <= merged[merged.length - 1].end + 0.01) {
      merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, row.end);
    } else {
      merged.push({ ...row });
    }
  });
  return merged;
}

export function wordTimingsLookEstimated(words, duration) {
  if (!Array.isArray(words) || !words.length) return true;
  const dur = Math.max(0.25, Number(duration) || 0.25);
  const firstStart = Number(words[0].start) || 0;
  const lastEnd = Number(words[words.length - 1].end) || 0;
  if (firstStart > 0.12 || lastEnd < dur - 0.12) return false;
  if (words.length === 1) return true;
  let maxGap = 0;
  let prevEnd = Number(words[0].end) || 0;
  for (let i = 1; i < words.length; i += 1) {
    const start = Number(words[i].start) || 0;
    maxGap = Math.max(maxGap, start - prevEnd);
    prevEnd = Number(words[i].end) || start;
  }
  return maxGap < 0.15;
}

export function keepWindowsFromSilences(duration, silences, minPause, pad) {
  const dur = Math.max(0.25, Number(duration) || 0.25);
  const full = [{ start: 0, end: Math.round(dur * 1000) / 1000 }];
  const cuts = [];
  (Array.isArray(silences) ? silences : []).forEach((row) => {
    const start = Math.max(0, Number(row?.start) || 0);
    const end = Math.min(dur, Number(row?.end) || 0);
    if (end - start + 1e-6 < (Number(minPause) || 0)) return;
    if (cuts.length && start <= cuts[cuts.length - 1][1] + 0.01) {
      cuts[cuts.length - 1][1] = Math.max(cuts[cuts.length - 1][1], end);
    } else {
      cuts.push([start, end]);
    }
  });
  if (!cuts.length) return full;

  const raw = [];
  let cursor = 0;
  cuts.forEach(([start, end]) => {
    if (start - cursor >= 0.08) raw.push([cursor, start]);
    cursor = Math.max(cursor, end);
  });
  if (dur - cursor >= 0.08) raw.push([cursor, dur]);
  if (!raw.length) return full;

  const padSec = Math.max(0, Number(pad) || 0);
  const padded = [];
  raw.forEach(([start, end]) => {
    let left = start <= 0.02 ? 0 : Math.max(0, start - padSec);
    const right = end >= dur - 0.02 ? dur : Math.min(dur, end + padSec);
    if (padded.length) left = Math.max(left, padded[padded.length - 1][1]);
    if (right - left >= 0.08) padded.push([left, right]);
  });
  const windows = padded
    .filter(([start, end]) => end - start >= MIN_KEEP_SECONDS)
    .map(([start, end]) => ({
      start: Math.round(start * 1000) / 1000,
      end: Math.round(end * 1000) / 1000,
    }));
  return windows.length ? windows : full;
}

export function keepWindowsForSnippet(snippet, pauseCut) {
  const duration = snippetDurationSeconds(snippet);
  const full = [{ start: 0, end: Math.round(duration * 1000) / 1000 }];
  const settings = sanitizePauseCut(pauseCut);
  if (!settings.enabled) return full;

  const silences = parseSilenceIntervals(snippet?.silence_intervals, duration);
  if (snippet?.silence_analyzed || silences.length) {
    return keepWindowsFromSilences(
      duration,
      silences,
      settings.min_pause_seconds,
      settings.pad_seconds,
    );
  }

  const words = parseWordTimings(snippet.word_timestamps, duration);
  if (wordTimingsLookEstimated(words, duration)) return full;
  const hasEdgeSilence = words.length
    && (words[0].start > 0.2 || duration - words[words.length - 1].end > 0.2);
  if (words.length < 2 && !hasEdgeSilence) return full;

  const pad = settings.pad_seconds;
  const minPause = settings.min_pause_seconds;
  const spans = [];
  words.forEach((word) => {
    const start = Math.max(0, word.start - pad);
    const end = Math.min(duration, word.end + pad);
    if (end - start < 0.05) return;
    if (!spans.length || start > spans[spans.length - 1][1] + 0.001) {
      const gap = spans.length ? start - spans[spans.length - 1][1] : 0;
      if (spans.length && gap < minPause) {
        spans[spans.length - 1][1] = Math.max(spans[spans.length - 1][1], end);
      } else {
        spans.push([start, end]);
      }
    } else {
      spans[spans.length - 1][1] = Math.max(spans[spans.length - 1][1], end);
    }
  });

  const windows = spans
    .filter(([start, end]) => end - start >= MIN_KEEP_SECONDS)
    .map(([start, end]) => ({
      start: Math.round(start * 1000) / 1000,
      end: Math.round(end * 1000) / 1000,
    }));
  return windows.length ? windows : full;
}

export function keptDuration(windows) {
  return Math.round(
    (Array.isArray(windows) ? windows : []).reduce(
      (sum, win) => sum + Math.max(0, Number(win?.end || 0) - Number(win?.start || 0)),
      0,
    ) * 1000,
  ) / 1000;
}

export function removedSeconds(duration, windows) {
  return Math.round(Math.max(0, Number(duration || 0) - keptDuration(windows)) * 1000) / 1000;
}

export function originalToCutTime(timeSec, windows) {
  const t = Math.max(0, Number(timeSec) || 0);
  let acc = 0;
  const list = Array.isArray(windows) ? windows : [];
  for (let i = 0; i < list.length; i += 1) {
    const start = Number(list[i].start) || 0;
    const end = Number(list[i].end) || 0;
    if (t < start) return Math.round(acc * 1000) / 1000;
    if (t <= end) return Math.round((acc + (t - start)) * 1000) / 1000;
    acc += Math.max(0, end - start);
  }
  return Math.round(acc * 1000) / 1000;
}

export function remapWordTimings(words, windows) {
  const out = [];
  (Array.isArray(words) ? words : []).forEach((word) => {
    if (!word || typeof word !== 'object') return;
    const token = String(word.word || '').trim();
    if (!token) return;
    const start = originalToCutTime(word.start || 0, windows);
    let end = originalToCutTime(word.end || word.start || 0, windows);
    if (end - start < 0.04) end = start + 0.08;
    out.push({ word: token, start, end: Math.round(end * 1000) / 1000 });
  });
  return out;
}

export function pauseCutSummary(snippets, pauseCut) {
  const settings = sanitizePauseCut(pauseCut);
  let clips = 0;
  let pauses = 0;
  let removed = 0;
  let original = 0;
  let kept = 0;
  (Array.isArray(snippets) ? snippets : []).forEach((snip) => {
    const duration = snippetDurationSeconds(snip);
    const windows = keepWindowsForSnippet(snip, settings);
    original += duration;
    kept += keptDuration(windows);
    const cutAmt = removedSeconds(duration, windows);
    if (cutAmt >= 0.08) {
      clips += 1;
      pauses += Math.max(0, windows.length - 1);
      if (windows[0]?.start > 0.05) pauses += 1;
      if (windows[windows.length - 1]?.end < duration - 0.05) pauses += 1;
      removed += cutAmt;
    }
  });
  return {
    ...settings,
    clips_trimmed: clips,
    pauses_removed: pauses,
    removed_seconds: Math.round(removed * 100) / 100,
    original_seconds: Math.round(original * 100) / 100,
    kept_seconds: Math.round(kept * 100) / 100,
  };
}

export function formatSecondsShort(value) {
  const n = Math.max(0, Number(value) || 0);
  if (n < 10) return `${n.toFixed(1)}s`;
  return `${Math.round(n)}s`;
}
